import { Address, latLngSchema, LocationTracking } from '@hero/schema';
import {
  LocationDistanceAndAddressData,
  API,
  SearchLocationData,
  AutocompleteAPIData,
  LocationMultiDistanceData,
  LocationMultiDestinationDistanceData,
  TransistorSoftLocationTrackingData,
} from '../types';
import { z } from 'zod';

//* search
export const locationSearchSchema = z.object({
  search: z
    .string()
    .trim()
    .describe(
      'Free-text query passed to the Google Places `findPlaceFromText` endpoint. Returns coordinates + address components.',
    ),
});
export type LocationSearchAPI = API<z.infer<typeof locationSearchSchema>, SearchLocationData[]>;

// current user location tracking
export const locationCurrentUserTrackingAPISchema = z.any();

export type LocationCurrentUserTrackingAPI = API<
  TransistorSoftLocationTrackingData,
  LocationTracking
>;

//* autocomplete
export const locationAutocompleteSchema = z.object({
  search: z
    .string()
    .trim()
    .describe(
      'Partial input passed to the Google Places autocomplete endpoint. Returns prediction list + place ids for follow-up `geocode_text` resolution.',
    ),
});
export type locationAutocompleteAPI = API<
  z.infer<typeof locationAutocompleteSchema>,
  AutocompleteAPIData
>;

//* address
export const addressAPISchema = latLngSchema;
/** this API returns all address components such as (full address name, state, city, country, etc) */
export type AddressAPI = API<z.infer<typeof addressAPISchema>, Address>;

//* distance + address API
export const locationDistanceSchema = z.object({
  origin: latLngSchema.describe(
    'Origin `{ lat, lng }` for the distance computation. Resolve from `resolve_location_point` when the caller gave free-text.',
  ),
  destination: latLngSchema.describe(
    'Destination `{ lat, lng }` for the distance computation. Resolve from `resolve_location_point` when the caller gave free-text.',
  ),
});

export const locationMultiDestinationDistanceSchema = z.object({
  origin: latLngSchema,
  destinations: z.array(latLngSchema).min(1),
});

export type LocationMultiDestinationDistanceAPI = API<
  z.infer<typeof locationMultiDestinationDistanceSchema>,
  LocationMultiDestinationDistanceData[]
>;

/** This API is for getting the distance & duration & address info for two locations such as origin & destination */
export type locationDistanceAndAddressAPI = API<
  z.infer<typeof locationDistanceSchema>,
  LocationDistanceAndAddressData
>;

// multi distance
export const locationMultiDistanceAPISchema = z.object({
  /** the locations/points of lat and lng to get the distance for.
   *
   * **NOTE** the first location is always considered the `origin`, and the reset of the array is considered `destinations`.
   *
   * For example, locations `[A, B, C]`
   *
   * the distance will be calculated:
   * - from `A` (origin) to `B` (destination)
   * - then from `B` (destination) to `C` (destination)
   *
   */
  locations: z.array(latLngSchema).min(2),
});
export type LocationMultiDistanceAPI = API<
  z.infer<typeof locationMultiDistanceAPISchema>,
  LocationMultiDistanceData
>;
